/**
 * Hidden letters — the PWA side of the secret-code vault (hidden.service.ts).
 *
 * The member types their code into the "write to someone new" search; a match
 * opens the vault for a while (the token lives in memory only). While it is
 * open, hidden pairs come back on the list, marked with a small wax-seal lock,
 * and a banner offers to lock them away again.
 */

import { useCallback, useEffect, useState } from 'react';
import { Info, Lock, LockOpen } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { lettersApi } from '../letters/api';
import {
  relationshipsApi,
  useHiddenStatusQuery,
  useHideMutation,
  useSetHiddenCodeMutation,
  useUnhideMutation,
  type UnlockResult,
} from '../relationships/api';
import { locked, unlocked } from './hiddenSlice';
import { track } from '../onboarding/track';

/** Search text worth trying as a code: not a handle, long enough to be one. */
export function looksLikeCode(text: string): boolean {
  const t = text.trim();
  return t.length >= 4 && t.length <= 32 && !t.startsWith('@');
}

function errorMessage(e: unknown, fallback: string): string {
  const err = e as { data?: { message?: string | string[] } };
  const m = err?.data?.message;
  return Array.isArray(m) ? m[0] ?? fallback : m ?? fallback;
}

/** Open / close the vault, refetching everything a token changes. */
export function useVault() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => Boolean(s.hidden.token));
  const refresh = useCallback(() => {
    dispatch(relationshipsApi.util.invalidateTags(['Relationship', 'Hidden', 'Note', 'Met']));
    dispatch(lettersApi.util.invalidateTags(['Letter']));
  }, [dispatch]);
  const open = useCallback(
    (r: UnlockResult) => {
      dispatch(unlocked(r));
      track({ event: 'correspondence_unlocked' });
      refresh();
    },
    [dispatch, refresh],
  );
  const lock = useCallback(() => {
    dispatch(locked());
    track({ event: 'correspondence_locked' });
    refresh();
  }, [dispatch, refresh]);
  return { isOpen, open, lock };
}

/** Locks the vault when its token lapses, so the list never shows a dead key. */
export function useHiddenAutoLock() {
  const expiresAt = useAppSelector((s) => s.hidden.expiresAt);
  const { lock } = useVault();
  useEffect(() => {
    if (!expiresAt) return;
    const ms = new Date(expiresAt).getTime() - Date.now();
    const t = setTimeout(lock, Math.max(0, ms));
    return () => clearTimeout(t);
  }, [expiresAt, lock]);
}

/** Above the list while the vault is open. */
export function VaultBanner() {
  const { isOpen, lock } = useVault();
  if (!isOpen) return null;
  return (
    <div className="vault-banner" role="status">
      <LockOpen size={15} aria-hidden="true" />
      <span className="vault-banner__text">Your hidden letters are showing.</span>
      <button type="button" className="btn btn--ghost vault-banner__lock" onClick={lock}>
        <Lock size={13} aria-hidden="true" /> Lock
      </button>
    </div>
  );
}

/** The small seal on a hidden row. */
export function HiddenPill() {
  return (
    <span className="pill pill--hidden" title="Hidden behind your secret code">
      <Lock size={10} aria-hidden="true" /> hidden
    </span>
  );
}

/**
 * New code (and confirmation). `currentCode` is offered when a code exists;
 * leaving it blank is the forgotten-code reset, which the server allows only
 * after a fresh sign-in.
 */
function CodeForm({
  hasCode,
  submitLabel,
  onDone,
}: {
  hasCode: boolean;
  submitLabel: string;
  onDone: (r: UnlockResult) => void | Promise<void>;
}) {
  const [setCode, { isLoading }] = useSetHiddenCodeMutation();
  const [current, setCurrent] = useState('');
  const [code, setCodeText] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);
    if (!looksLikeCode(code)) {
      setError("Pick 4 to 32 characters, not starting with @ — something you'd never search for.");
      return;
    }
    if (code.trim().toLowerCase() !== confirm.trim().toLowerCase()) {
      setError("Those two don't match.");
      return;
    }
    try {
      const r = await setCode({ code, currentCode: current.trim() || undefined }).unwrap();
      setCurrent('');
      setCodeText('');
      setConfirm('');
      await onDone(r);
    } catch (e) {
      setError(errorMessage(e, 'Could not save your secret code'));
    }
  };

  return (
    <div className="vault-form">
      {hasCode && (
        <label className="field">
          <span className="field__label">Current code</span>
          <input
            className="field__input"
            type="password"
            autoComplete="off"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Forgotten? Leave blank right after signing in"
            maxLength={32}
          />
        </label>
      )}
      <label className="field">
        <span className="field__label">{hasCode ? 'New code' : 'Secret code'}</span>
        <input
          className="field__input"
          type="password"
          autoComplete="off"
          value={code}
          onChange={(e) => setCodeText(e.target.value)}
          placeholder="A word you'd never search for"
          maxLength={32}
        />
      </label>
      <label className="field">
        <span className="field__label">Once more</span>
        <input
          className="field__input"
          type="password"
          autoComplete="off"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          maxLength={32}
        />
      </label>
      {error && (
        <p className="form-error" role="alert">
          <Info size={13} aria-hidden="true" /> {error}
        </p>
      )}
      <button
        type="button"
        className="btn btn--primary"
        disabled={isLoading || !code || !confirm}
        onClick={() => void onSubmit()}
      >
        {isLoading ? 'Sealing…' : submitLabel}
      </button>
    </div>
  );
}

/**
 * On a relationship's summary: hide it behind the code (setting one first if
 * needed), or — while unlocked — put it back on the list.
 */
export function HideCard({
  relationshipId,
  peerName,
  hidden,
  onHidden,
}: {
  relationshipId: string;
  peerName: string;
  hidden: boolean;
  /** Called after hiding while the vault is closed — the pair just left the list. */
  onHidden: () => void;
}) {
  const { data: status } = useHiddenStatusQuery();
  const { isOpen, open } = useVault();
  const [hide, { isLoading: hiding }] = useHideMutation();
  const [unhide, { isLoading: unhiding }] = useUnhideMutation();
  const [settingCode, setSettingCode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doHide = async (stayOpen: boolean) => {
    setError(null);
    try {
      await hide({ relationshipId }).unwrap();
      if (!stayOpen) onHidden();
    } catch (e) {
      setError(errorMessage(e, 'Could not hide this conversation'));
    }
  };

  const doUnhide = async () => {
    setError(null);
    try {
      await unhide({ relationshipId }).unwrap();
    } catch (e) {
      setError(errorMessage(e, 'Could not bring this conversation back'));
    }
  };

  return (
    <div className="contacts-card vault-card">
      <div className="contacts-card__head">
        <span className="contacts-card__title">
          <Lock size={13} aria-hidden="true" /> {hidden ? 'Hidden' : 'Keep it private'}
        </span>
        <span className="contacts-card__hint">only on your side</span>
      </div>
      {hidden ? (
        <>
          <p className="vault-card__copy">
            Your letters with @{peerName} stay off your list until you type your secret code into
            search. @{peerName} sees no difference.
          </p>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={unhiding}
            onClick={() => void doUnhide()}
          >
            {unhiding ? 'Bringing it back…' : 'Show on my list again'}
          </button>
        </>
      ) : settingCode ? (
        <>
          <p className="vault-card__copy">
            Choose a secret code first. To find hidden letters again, type it where you search for
            someone new.
          </p>
          <CodeForm
            hasCode={false}
            submitLabel="Set code and hide"
            onDone={async (r) => {
              // Setting a code opens the vault, so the pair stays in view, marked.
              open(r);
              setSettingCode(false);
              await doHide(true);
            }}
          />
        </>
      ) : (
        <>
          <p className="vault-card__copy">
            Hide your letters with @{peerName} behind a secret code. They keep arriving; they just
            stay off your list until you type the code into search.
          </p>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={hiding || !status}
            onClick={() => (status?.hasCode ? void doHide(isOpen) : setSettingCode(true))}
          >
            {hiding ? 'Hiding…' : 'Hide this conversation'}
          </button>
        </>
      )}
      {error && (
        <p className="form-error" role="alert">
          <Info size={13} aria-hidden="true" /> {error}
        </p>
      )}
    </div>
  );
}

/** On the profile screen: set, change or reset the secret code. */
export function SecretCodeCard() {
  const { data: status } = useHiddenStatusQuery();
  const { isOpen, open } = useVault();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!status) return null;

  return (
    <section className="contacts-card vault-card" aria-label="Secret code">
      <div className="contacts-card__head">
        <span className="contacts-card__title">
          <Lock size={13} aria-hidden="true" /> Secret code
        </span>
        <span className="contacts-card__hint">{status.hasCode ? 'set' : 'not set'}</span>
      </div>
      <p className="vault-card__copy">
        {status.hasCode
          ? isOpen && status.hiddenCount != null
            ? `Unlocked — ${status.hiddenCount} hidden ${status.hiddenCount === 1 ? 'conversation' : 'conversations'}. `
            : 'Type it where you search for someone new to show hidden letters. '
          : 'Hide conversations from your list and bring them back by typing a code into search. '}
        {status.hasCode &&
          "Forgotten it? Sign out and back in, then set a new one here with the current code left blank. Hidden letters stay hidden."}
      </p>
      {saved && !editing && <p className="invite-friend__ok" role="status">Your new code is sealed.</p>}
      {editing ? (
        <CodeForm
          hasCode={status.hasCode}
          submitLabel={status.hasCode ? 'Change code' : 'Set code'}
          onDone={(r) => {
            open(r);
            setEditing(false);
            setSaved(true);
          }}
        />
      ) : (
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            setSaved(false);
            setEditing(true);
          }}
        >
          {status.hasCode ? 'Change secret code' : 'Set a secret code'}
        </button>
      )}
    </section>
  );
}
